import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import nbCommon from './locales/nb/common.json';
import esCommon from './locales/es/common.json';

// Activate the typed-keys augmentation defined in `./types`.
import './types';

/**
 * i18next bootstrap (W1-F14-S1, extended W22-F1-S2 with `es`).
 *
 * - `common` is the only namespace at W1; per-page namespaces are added in W4+.
 * - Detection: localStorage (`senviro.locale`) → navigator → fallback `en`.
 * - Eager-imports the JSON catalogs; lazy loading is deferred until catalogs
 *   exceed ~100KB per locale (likely W10+).
 *
 * Plural strategy (re-verified during W75-F6-S3): i18next v23 ships native
 * CLDR plural rules — keys ending in `_one` / `_other` (`buildingCount_one`,
 * `unreadCount_other`) resolve automatically through `t('key', { count })`.
 * We deliberately do **not** bundle `i18next-icu`: an experimental install
 * during the W75-F6 audit showed the ICU plugin coexists poorly with our
 * existing `{{ name }}` interpolations (it intercepts non-ICU placeholders
 * and breaks ~40 unrelated test files). i18next's native plural rules cover
 * every plural string in the catalog today; if a future feature genuinely
 * needs ICU-MessageFormat selectors, add a per-namespace ICU resource
 * loader (`i18next-icu` supports namespace scoping) rather than a global
 * `.use(ICU)`. See `docs/voice/2026-05-11-icu-formatters.md`.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'nb', 'es'],
    defaultNS: 'common',
    ns: ['common'],
    resources: {
      en: { common: enCommon },
      nb: { common: nbCommon },
      es: { common: esCommon },
    },
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // 2026-05-15 — dropped `navigator` from the order. Previously, any
      // user on a Norwegian-locale browser landed on `nb` even after
      // explicitly switching to English in Settings (their localStorage
      // would clear → navigator → `nb-NO` → mapped to `nb`). With `navigator`
      // gone the fallback is `fallbackLng: 'en'` whenever localStorage has
      // nothing, which is what users expect on a global advisory platform.
      order: ['localStorage'],
      lookupLocalStorage: 'senviro.locale',
      caches: ['localStorage'],
    },
    returnNull: false,
  });

export default i18n;

/**
 * Languages the Senviro web app currently ships catalogs for. The W10-F3-S2
 * Language tab + the W10-F1 Translations admin both source dropdowns from
 * this list. When Core's `/api/v1/reference/languages` ships, the FE can
 * supplement this list with extra entries; the catalogs themselves remain
 * the source of truth for which language renders without falling back.
 */
export interface SupportedLanguage {
  code: string;
  /** Native-language label so users see "Norsk bokmål" rather than "nb". */
  label: string;
}

export const supportedLanguages: ReadonlyArray<SupportedLanguage> = [
  { code: 'en', label: 'English' },
  { code: 'nb', label: 'Norsk bokmål' },
  { code: 'es', label: 'Español' },
];

/**
 * Switch the active i18next language and persist via the language detector
 * (which writes to `localStorage['senviro.locale']`). Returns the promise
 * so callers can await before navigating.
 */
export function changeI18nLanguage(code: string): Promise<unknown> {
  return Promise.resolve(i18n.changeLanguage(code));
}
